import { Avatar, Button, Card, Space, Tooltip, Typography } from "antd";
import { BankOutlined, CopyOutlined, EditOutlined } from "@ant-design/icons";
import { servicesUrlConfig } from "shared/config/setting";
import { renderRoleTags } from "../utils/renderHelpers";
import { BUTTON_STYLES } from "../constants";

const { Title, Paragraph, Text } = Typography;

/**
 * Header block for the Organization page: logo, name, description and the
 * current user's roles in that organization.
 *
 * @param {Object} orgEntry - {organization, roles} entry from UserOrgContext
 * @param {boolean} canEdit - Show the edit button (org admins / superusers)
 * @param {Function} onEditClick - Called when the edit button is pressed
 * @param {Function} translate - Translation function (i18n key -> string)
 */
export const OrgInfoHeader = ({
  orgEntry,
  canEdit = false,
  onEditClick,
  translate = (key) => key,
}) => {
  const organization = orgEntry?.organization ?? {};
  const roles = orgEntry?.roles ?? [];
  const logoUrl = organization.logo_path
    ? servicesUrlConfig.ROBOTS_API.getStaticUrl(organization.logo_path)
    : undefined;
  // org_id is a UUID4 — show only its first group, full value on hover / copy.
  const orgIdText = String(organization.org_id ?? "");
  const shortOrgId =
    orgIdText.length > 8 ? `${orgIdText.slice(0, 8)}…` : orgIdText;

  return (
    <Card
      variant="borderless"
      className="criclebox mb-24"
      styles={{ body: { padding: "12px 16px" } }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
        <Avatar
          shape="square"
          size={70}
          src={logoUrl}
          icon={<BankOutlined />}
          style={{ backgroundColor: logoUrl ? "transparent" : undefined }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Space size={8} align="center" wrap>
            <Title level={6} style={{ margin: 0 }}>
              {organization.name || organization.org_id}
            </Title>
            {organization.org_id ? (
              <Text
                type="secondary"
                copyable={{
                  text: organization.org_id,
                  icon: <CopyOutlined style={{ fontSize: 11 }} />,
                  tooltips: [
                    translate("organizations.copyOrgId"),
                    translate("organizations.orgIdCopied"),
                  ],
                }}
                style={{ fontSize: 11, fontFamily: "monospace" }}
              >
                {`(ID: ${shortOrgId}...)`}
              </Text>              
            ) : null}
            {roles.length > 0 ? (
              renderRoleTags(roles, translate)
            ) : (
              <Text type="secondary">-</Text>
            )}
          </Space>
          {organization.description ? (
            <Paragraph type="secondary" style={{ margin: "2px 0 0" }}>
              {organization.description}
            </Paragraph>
          ) : null}
        </div>
        {canEdit && (
          <Button icon={<EditOutlined />} onClick={onEditClick} style={BUTTON_STYLES.add}>
            {translate("organizations.editOrganization")}
          </Button>
        )}
      </div>
    </Card>
  );
};
